import type { Table } from 'dexie';

export type SyncerConfig<T extends { id: string }> = {
	name: string;
	table: () => Table<T, string>;
	fetch?: {
		many?: (opts: { where?: unknown }) => Promise<T[] | null>;
		one?: (id: string) => Promise<T | null>;
		aggregate?: () => Promise<{ count: number } | null>;
		ids?: () => Promise<{ id: string }[] | null>;
	};
	stream?: (
		max: string | null
	) => (on_batch: (rows: T[]) => void) => (() => void) | void;
	last_updated_key?: string;
	persist?: (rows: T[]) => Promise<void>;
	on_delete?: (ids: string[]) => void;
};

export type Syncer<T extends { id: string }> = {
	start: () => Promise<void>;
	populate: () => Promise<T[] | null>;
	subscribe: () => void;
	unsubscribe: () => void;
	refetch: (id: string) => Promise<void>;
	refresh: () => Promise<T[] | null>;
	resync: () => Promise<void>;
	prune: () => Promise<boolean>;
	local_max: () => Promise<string | null>;
	reset: () => Promise<void>;
};

export function create_syncer<T extends { id: string }>(config: SyncerConfig<T>): Syncer<T> {
	const last_updated_key = config.last_updated_key ?? 'date_updated';

	let active_unsubscribe: (() => void) | null = null;
	let subscribe_pending = false;

	async function local_max(): Promise<string | null> {
		const last = await config.table().orderBy(last_updated_key).last();
		const value = last ? last[last_updated_key as keyof T] : null;
		return typeof value === 'string' && value ? value : null;
	}

	async function populate_after(max: string | null): Promise<T[] | null> {
		const rows = await config.fetch?.many?.(max ? { where: { [last_updated_key]: { _gt: max } } } : {});
		if (!rows?.length) return rows ?? null;
		await config.persist?.(rows);
		return rows;
	}

	async function populate(): Promise<T[] | null> {
		return populate_after(await local_max());
	}

	function subscribe_with_anchor(max: string | null): void {
		const unsubscribe = config.stream?.(max)(async (rows) => {
			if (!rows?.length) return;
			await config.persist?.(rows);
		});
		active_unsubscribe = unsubscribe ?? null;
	}

	function subscribe(): void {
		// Idempotent (amendment A4): a second subscribe while one is active or
		// in flight is a no-op — prevents ws subscription leaks on repeated
		// lifecycle starts.
		if (active_unsubscribe || subscribe_pending) return;
		subscribe_pending = true;
		void local_max().then((max) => {
			subscribe_pending = false;
			if (active_unsubscribe) return;
			subscribe_with_anchor(max);
		});
	}

	function unsubscribe(): void {
		active_unsubscribe?.();
		active_unsubscribe = null;
	}

	async function start(): Promise<void> {
		const max = await local_max();
		await populate_after(max);
		subscribe_with_anchor(max);
		void prune();
	}

	async function refetch(id: string): Promise<void> {
		const row = await config.fetch?.one?.(id);
		if (!row) return;
		await config.persist?.([row]);
	}

	async function refresh(): Promise<T[] | null> {
		const rows = await config.fetch?.many?.({});
		if (!rows?.length) return rows ?? null;
		await config.persist?.(rows);
		return rows;
	}

	async function resync(): Promise<void> {
		await config.table().clear();
		await populate_after(null);
	}

	async function prune(): Promise<boolean> {
		const aggregate = await config.fetch?.aggregate?.();
		if (!aggregate) return false;
		const local_count = await config.table().count();
		if (local_count === 0 || local_count === aggregate.count) return false;

		if (local_count > aggregate.count) {
			const remote_ids = (await config.fetch?.ids?.())?.map((row) => row.id) ?? [];
			if (!remote_ids.length) return false;
			const local_ids = (await config.table().toCollection().primaryKeys()) as string[];
			const to_delete = local_ids.filter((id) => !remote_ids.includes(id));
			if (to_delete.length) {
				await config.table().bulkDelete(to_delete);
				config.on_delete?.(to_delete);
			}
			return true;
		}

		await resync();
		return true;
	}

	async function reset(): Promise<void> {
		unsubscribe();
		await config.table().clear();
	}

	return {
		start,
		populate,
		subscribe,
		unsubscribe,
		refetch,
		refresh,
		resync,
		prune,
		local_max,
		reset
	};
}
